<template>
	<v-dialog
		v-model="visible"
		:max-width="$vuetify.breakpoint.width <= 1024 ? maxWidth_xs : width"
		persistent
		:retain-focus="false"
	>
		<div class="dialog-container pb-3">
			<div class="top-line"></div>
			<v-row class="mt-2 px-8" no-gutters>
				<v-col cols="12" class="title-text">Email guests</v-col>
			</v-row>
			<v-row class="my-0 px-8">
				<v-col cols="12" class="pa-0">
					<v-alert class="mt-4" type="error" dense v-if="error_msg.length !== 0">
						{{ error_msg }}
					</v-alert>
				</v-col>
			</v-row>
			<v-row class="mt-3 px-8" no-gutters>
				<v-col cols="12" class="item-text">Invitees</v-col>
				<v-col cols="12">
					<v-combobox
						v-model="select_invitees"
						:items="invitees_items"
						:search-input.sync="invitees_search"
						:color="color"
						:loading="invitees_loading"
						append-icon=""
						background-color="#FAFAFA"
						item-text="employee.display_name"
						item-value="employee.id"
						item-color="success"
						item-disabled="disabled"
						placeholder="Type to search contact names"
						autofocus
						auto-select-first
						chips
						outlined
						dense
						hide-no-data
						return-object
						hide-details
						clearable
						multiple
						@change="onChange()"
					>
						<template v-slot:selection="data">
							<v-chip
								v-bind="data.attrs"
								:input-value="data.selected"
								close
								label
								@click:close="onRemoveInvitee(data.item.employee.id)"
							>
								{{ formatName(data.item.employee.display_name) }}
							</v-chip>
						</template>	
						<template v-slot:item="{item, on, attrs}">
							<v-list-item v-on="on" dense>
								<v-row align="start" class="ma-0">
									<v-simple-checkbox
										v-on="on"
										:value="attrs.inputValue"
										:ripple="false"
										:color="color"
										dense
									></v-simple-checkbox>
									<v-user-avatar
										:class="item.employee.email.length !== 0 ? 'mt-2':''"
										size="24"
										class="mx-3 text-caption text-decoration-none"
										:code="formatName(item.employee.display_name)"
										:image="`${item.uri}/image.png`"
									/>									
									<div>
										<div class="item-text">{{ formatName(item.employee.display_name) }}</div>
										<div v-if="item.employee.email.length !== 0" class="item-text over-text" style="color: rgba(25, 42, 62, 0.7); font-size: 12px;">{{ item.employee.email }}</div>
									</div>
								</v-row>
							</v-list-item>
						</template>							
					</v-combobox>
				</v-col>
			</v-row>
			<v-row class="mt-3 mb-2 px-8" justify="end" no-gutters>
				<div>
					<v-btn outlined class="text-capitalize" @click="$emit('cancel')">Cancel</v-btn>
					<v-btn 
						depressed 
						color="#00AAFD" 
						class="ml-2 white--text text-capitalize"
						:disabled="select_invitees.length === 0"
						@click="onSend(select_invitees)">
						<span>Send</span>
					</v-btn>
				</div>
			</v-row>
		</div>
	</v-dialog>
</template>

<script>
/** 0-invited, 1-accepted, 2-rejected */
const Invited_Code = 0;
const DescriptionLimit = 60;

export default {
	name: 'EmailDialog',
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		color: {
			type: String,
			default: ''
		},
		eventItem: {
			type: Object,
			default: null
		}
	},
	data() {
		this.maxWidth_xs = '100%';
		this.width = '800';

		return {
			error_msg: '',
			invitees_loading: false,
			invitees_search: null,

			select_invitees: [],
			invitees_items: [],
		}
	},
	watch: {
		invitees_search(val) {
			val && this.inviteesQuery(val);
		}
	},
	methods: {
		inviteesQuery(key) {
			this.invitees_loading = true;

			const params = [];
			params.push("name=" + encodeURIComponent(key));
			params.push("allow_all=" + true);
			params.push("find_employee=" + true);
			params.push("find_freelancer=" + true);

			let url = "/api/addressbook/email";
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}

			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const json = val.data;

					if (typeof(json) == "string") {
						json = JSON.parse(json);
					}
					const { status, data } = json;

					if (status == "OK") {
						const existed_contact = this.eventItem.invitees.filter(invite => { return invite.contact !== null });
						const existed_employee = this.eventItem.invitees.filter(invite => { return invite.employee !== null });

						const existed_contact_ids = existed_contact.map(invite => { return invite.contact.id });
						const existed_employee_ids = existed_employee.map(invite => { return invite.employee.id });
							
						const un_existed = data.filter(d => { 
							return d.contact_id ? !existed_contact_ids.includes(d.contact_id) : !existed_employee_ids.includes(d.abook_id)
						});

						const item_tmp = un_existed.map(d => {
							const name = this.formatName(d.name);
							const description = name.length > DescriptionLimit ? name.slice(0, DescriptionLimit) + '...' : name;

							const invite_obj = {
								id: 0,
								accepted: Invited_Code,
								contact: null,
								employee: {
									id: d.contact_id ? d.contact_id : d.abook_id,
									contact_id: d.contact_id ? d.contact_id : null,
									code: d.code,
									display_name: description,
									email: d.email,
									send_email: true
								},
								image: `${d.uri}/image.png`,
								uri: d.uri
							};

							return invite_obj;
						});

						this.invitees_items = item_tmp;
					} 
				})
				.catch((e) => {
					this.error_msg = e.message;
				})
				.finally(() => {
					this.invitees_loading = false
				});
		},
		onRemoveInvitee(id) {
			this.select_invitees = this.select_invitees.filter(invitee => { return invitee.employee.id !== id });
		},
		onSend(select_invitees) {
			this.$emit('send', select_invitees)
		},
		formatName(name) {
			let display_name = '';

			if (name && name.includes(',')) {
				const name_arr = name.split(',');
				if (name_arr[1].includes('(')) {
					const name_tmp = name_arr[1];
					const index = name_tmp.indexOf('(');
					const first_name = name_tmp.substring(0, index-1).trim();
					const last_name = name_arr[0].trim();
					display_name = `${first_name} ${last_name} ${name_tmp.substring(index, name_tmp.length)}`;
				} else {
					const first_name = name_arr[1].trim();
					const last_name = name_arr[0].trim();
					display_name = `${first_name} ${last_name}`;
				}
			} else {
				display_name = name;
			}

			return display_name;
		},
		onChange() {
			this.invitees_items = [];
		}
	}
}
</script>

<style lang="scss" scoped>
.dialog-container {
	background: #FFFFFF;
  border-radius: 4px;
}
.top-line {
  width: 100%;
  height: 8px;
  background: #00AAFD;
  border-radius: 4px 4px 0px 0px;
}
.title-text {
  font-family: Roboto;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  color: #192A3E;
}
.item-text {
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: rgba(55, 73, 95, 1);
}
</style>

